import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FlagImport from 'react-world-flags'
import { Button } from '@/components/shadcn/button'
import { Checkbox } from '@/components/shadcn/checkbox'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { Input } from '@/components/shadcn/input'
import { useAuthStore } from '@/store/auth.store'
import { Bot } from 'lucide-react'
import type { PlannedLanguages } from './types'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { RichTextEditor } from '@/components/common/RichTextEditor'
import { cn } from '@/lib/utils'

const Flag = ((FlagImport as unknown as { default?: typeof FlagImport }).default ??
  FlagImport) as typeof FlagImport

const FLAG_CODES: Record<string, string> = {
  en: 'gb', el: 'gr', cs: 'cz', uk: 'ua', he: 'il', zh: 'cn',
  fa: 'ir', sr: 'rs', ar: 'sa', da: 'dk', sv: 'se', et: 'ee', sl: 'si',
}

interface TranslationsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  value: Record<string, string>
  onApply: (translations: Record<string, string>) => void
  plannedLanguages?: PlannedLanguages
  onPlannedChange?: (planned: PlannedLanguages) => void
  autoTranslateAll?: boolean
  /** The original source text shown read-only on the right side */
  sourceValue?: string
  /** When true, renders RichTextEditor instead of plain Input fields */
  isRichText?: boolean
}

export function TranslationsDialog({
  open,
  onOpenChange,
  title,
  value,
  onApply,
  plannedLanguages,
  onPlannedChange,
  autoTranslateAll = false,
  sourceValue,
  isRichText = false,
}: TranslationsDialogProps) {
  const { t, i18n } = useTranslation('common')
  const supportedLanguages = useAuthStore((s) => s.supportedLanguages)
  const [local, setLocal] = useState<Record<string, string>>(value)
  const [flagError, setFlagError] = useState<Record<string, boolean>>({})
  const [selectedLang, setSelectedLang] = useState<string>(supportedLanguages[0] ?? '')
  useEffect(() => {
    if (open) {
      setLocal(value)
      setSelectedLang(supportedLanguages[0] ?? '')
    }
  }, [open, value, supportedLanguages])

  const displayLang = i18n.resolvedLanguage ?? i18n.language
  const languageName = (lang: string) => {
    try {
      return new Intl.DisplayNames([displayLang], { type: 'language' }).of(lang) ?? lang
    } catch {
      return lang
    }
  }

  const handleApply = () => {
    onApply(local)
    onOpenChange(false)
  }

  const showAutoTranslate = plannedLanguages !== undefined
    && onPlannedChange !== undefined
    && !autoTranslateAll

  const currentValue = local[selectedLang] ?? ''
  const isPlanned = plannedLanguages?.has(selectedLang) ?? false
  const hasValue = !!currentValue.trim()

  function togglePlanned(lang: string) {
    if (!onPlannedChange || !plannedLanguages) return
    const next = new Set(plannedLanguages)
    if (next.has(lang)) {
      next.delete(lang)
    } else {
      next.add(lang)
    }
    onPlannedChange(next)
  }

  function handleChange(newValue: string) {
    setLocal((prev) => ({ ...prev, [selectedLang]: newValue }))
    // If user types, remove from planned
    if (newValue.trim() && onPlannedChange && plannedLanguages?.has(selectedLang)) {
      const next = new Set(plannedLanguages)
      next.delete(selectedLang)
      onPlannedChange(next)
    }
  }

  const autoTranslatableLangs = supportedLanguages.filter((lang) => !local[lang]?.trim())
  const allPlanned = autoTranslatableLangs.length > 0 &&
    autoTranslatableLangs.every((lang) => plannedLanguages?.has(lang))

  function toggleAll() {
    if (!onPlannedChange) return
    const next = new Set(plannedLanguages)
    if (allPlanned) {
      autoTranslatableLangs.forEach((lang) => next.delete(lang))
    } else {
      autoTranslatableLangs.forEach((lang) => next.add(lang))
    }
    onPlannedChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[80vh] flex-col gap-0 p-0 sm:max-w-4xl">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-4">
          <DialogTitle>{title ?? t('translations.editTranslations')}</DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 overflow-hidden">
          {/* Left sidebar: language tabs — fixed, scrollable */}
          <div className="flex w-44 shrink-0 flex-col overflow-y-auto border-r">
            {supportedLanguages.map((lang) => {
              const langHasValue = !!local[lang]?.trim()
              const isSelected = lang === selectedLang
              const langIsPlanned = plannedLanguages?.has(lang) ?? false

              return (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setSelectedLang(lang)}
                  className={cn(
                    'relative flex items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-muted/60',
                    isSelected && 'bg-muted',
                  )}
                >
                  {/* Status dot */}
                  <span
                    className={cn(
                      'absolute right-1.5 top-1.5 size-1.5 rounded-full',
                      langHasValue ? 'bg-green-500' : 'bg-muted-foreground/30',
                    )}
                  />

                  {flagError[lang] ? (
                    <span className="w-7 shrink-0 text-center text-xs font-medium uppercase text-muted-foreground">
                      {lang}
                    </span>
                  ) : (
                    <Flag
                      code={FLAG_CODES[lang.toLowerCase()] ?? lang}
                      style={{ width: 28, height: 18 }}
                      className="shrink-0 rounded-sm object-cover"
                      onError={() => setFlagError((prev) => ({ ...prev, [lang]: true }))}
                    />
                  )}

                  <span className="text-sm">{languageName(lang)}</span>

                  {/* Auto-translate indicator in tab */}
                  {!langHasValue && (autoTranslateAll || (showAutoTranslate && langIsPlanned)) && (
                    <Bot className="ml-auto h-3.5 w-3.5 shrink-0 text-primary" />
                  )}
                </button>
              )
            })}
          </div>

          {/* Main content: translation editor + source */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="flex min-h-0 flex-1">
              {/* Left: translation editor */}
              <div className={`flex flex-col gap-2 p-4 ${sourceValue !== undefined ? 'w-1/2 shrink-0 border-r' : 'flex-1'}`}>
                <div className="flex shrink-0 items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">
                    {languageName(selectedLang)}
                  </span>
                  {showAutoTranslate && !hasValue && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1.5">
                          <Checkbox
                            id={`auto-${selectedLang}`}
                            checked={isPlanned}
                            onCheckedChange={() => togglePlanned(selectedLang)}
                          />
                          <label
                            htmlFor={`auto-${selectedLang}`}
                            className="flex cursor-pointer items-center gap-1 text-xs text-muted-foreground"
                          >
                            <Bot className="h-3.5 w-3.5" />
                            {t('translations.autoTranslate')}
                          </label>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-56 text-center">
                        <p className="font-medium">{t('translations.autoTooltipTitle')}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {t('translations.autoTooltipDisclaimer')}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>

                {isRichText ? (
                  <RichTextEditor
                    value={currentValue}
                    onChange={handleChange}
                    placeholder={t('translations.translationPlaceholder')}
                    className="min-h-0 flex-1"
                  />
                ) : (
                  <Input
                    placeholder={t('translations.translationPlaceholder')}
                    value={currentValue}
                    onChange={(e) => handleChange(e.target.value)}
                  />
                )}
              </div>

              {/* Right: original source (read-only) */}
              {sourceValue !== undefined && (
                <div className="flex w-1/2 shrink-0 flex-col gap-2 p-4">
                  <span className="text-sm font-medium text-muted-foreground shrink-0">
                    {t('translations.originalText')}
                  </span>
                  {isRichText ? (
                    <RichTextEditor
                      value={sourceValue}
                      onChange={() => {}}
                      className="min-h-0 flex-1"
                      disabled
                    />
                  ) : (
                    <Input
                      value={sourceValue}
                      readOnly
                      className="cursor-text bg-muted/40"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 border-t px-6 py-4">
          {showAutoTranslate && (
            <Button
              type="button"
              variant={allPlanned ? 'default' : 'outline'}
              size="sm"
              onClick={toggleAll}
              disabled={autoTranslatableLangs.length === 0}
            >
              <Bot className="mr-2 h-4 w-4 shrink-0" />
              {allPlanned
                ? t('translations.autoUnplanAll')
                : t('translations.autoSelectAll')
              }
            </Button>
          )}
          <div className="ml-auto flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('buttons.cancel')}
            </Button>
            <Button type="button" onClick={handleApply}>
              {t('buttons.apply')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
